import { create } from 'zustand';

import { getView, type PrismMode } from '../engine/prism.js';
import { clearOverlay, setOverlay } from '../engine/host.js';
import { docIO, SAVE_DELAY_MS } from '../lib/doc-io.js';
import {
  emptyCell,
  flipCell,
  getPixel,
  newPixelDoc,
  PALETTE_CHARS,
  parseHex,
  parsePixelDoc,
  resizeCell,
  serializePixelDoc,
  setPixel,
  shiftCell,
  toHex,
  usedColors,
  type PixelAnimation,
  type PixelDoc,
  type PixelLayer,
  type RGBA,
} from '../lib/pixel-doc.js';
import { logEditor } from './console.js';
import { useWorkspace } from './workspace.js';

/**
 * The open pixel art document (`*.pixel.json`).
 *
 * Edits replace cells immutably (unchanged cells are shared), so undo history
 * is a list of documents. Every committed edit is pushed to the engine views
 * through the host overlay, so sprites using the art update live in the
 * Scene and Play tabs; the file is saved after a short debounce.
 */

/** Undo steps kept: up to 200, fewer for big canvases (about 512 MB of cells). */
function historyLimit(doc: PixelDoc | null): number {
  const bytes = doc ? doc.width * doc.height * 4 : 1;
  return Math.max(8, Math.min(200, Math.floor((512 * 1024 * 1024) / bytes)));
}

export type PixelTool = 'pencil' | 'eraser' | 'fill' | 'line' | 'rect' | 'ellipse' | 'picker' | 'select' | 'move';
export type SaveState = 'saved' | 'pending' | 'saving' | 'error';

export interface PixelSelection {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PixelClipboard {
  w: number;
  h: number;
  pixels: Uint8ClampedArray;
}

export interface PixelState {
  path: string | null;
  doc: PixelDoc | null;
  loading: boolean;
  error: string | null;
  saveState: SaveState;
  past: PixelDoc[];
  future: PixelDoc[];

  frame: number;
  layer: number;
  tool: PixelTool;
  color: RGBA;
  secondary: RGBA;
  brush: number;
  mirrorX: boolean;
  mirrorY: boolean;
  /** Rectangle / ellipse tools draw filled shapes. */
  filled: boolean;
  /** Fill only touching pixels (otherwise every pixel of that color). */
  contiguous: boolean;
  zoom: number;
  grid: boolean;
  onion: boolean;
  /** Repeat the frame around the canvas to check that a texture tiles seamlessly. */
  tiled: boolean;
  playing: boolean;
  /** Animation previewed by the player (null = every frame in order). */
  previewAnimation: string | null;
  selection: PixelSelection | null;
  clipboard: PixelClipboard | null;

  open: (path: string) => Promise<void>;
  create: (path: string, width: number, height: number, init?: (doc: PixelDoc) => PixelDoc) => Promise<void>;
  close: () => void;
  flush: () => Promise<void>;
  externalChange: (path: string) => Promise<void>;

  /** Replace the document; `history: false` for live stroke previews. */
  commit: (doc: PixelDoc, options?: { history?: boolean; base?: PixelDoc }) => void;
  undo: () => void;
  redo: () => void;

  set: (patch: Partial<Pick<PixelState, 'tool' | 'color' | 'secondary' | 'brush' | 'mirrorX' | 'mirrorY' | 'filled' | 'contiguous' | 'zoom' | 'grid' | 'onion' | 'tiled' | 'playing' | 'previewAnimation' | 'selection'>>) => void;
  setFrame: (frame: number) => void;
  setLayer: (layer: number) => void;
  swapColors: () => void;

  addFrame: (duplicate: boolean) => void;
  deleteFrame: () => void;
  moveFrame: (delta: number) => void;
  addLayer: () => void;
  deleteLayer: () => void;
  moveLayer: (delta: number) => void;
  setLayerProps: (index: number, patch: Partial<PixelLayer>) => void;
  mergeDown: () => void;

  addPaletteColor: (hex: string) => void;
  removePaletteColor: (index: number) => void;
  setPaletteColor: (index: number, hex: string) => void;
  paletteFromArt: () => void;

  setAnimation: (name: string, anim: PixelAnimation | null) => void;
  renameAnimation: (from: string, to: string) => void;
  setPivot: (pivot: [number, number] | undefined) => void;
  setFilter: (filter: 'nearest' | 'linear' | undefined) => void;

  resize: (width: number, height: number, anchor: 'top-left' | 'center' | 'bottom') => void;
  flip: (horizontal: boolean, allFrames: boolean) => void;
  shift: (dx: number, dy: number) => void;

  copySelection: () => void;
  cutSelection: () => void;
  deleteSelection: () => void;
  paste: () => void;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
const lastDisk = new Map<string, string>();

function projectId(): number | null {
  return useWorkspace.getState().projectId;
}

function pushToViews(path: string, text: string): void {
  setOverlay(path, text);
  for (const mode of ['scene', 'model', 'play'] as PrismMode[]) void getView(mode)?.fileChanged(path);
}

/** Replace one cell; every other cell is shared with `doc`. */
export function withCell(doc: PixelDoc, frame: number, layer: number, cell: Uint8ClampedArray): PixelDoc {
  const cells = doc.cells.map((f, i) => (i === frame ? f.map((c, j) => (j === layer ? cell : c)) : f));
  return { ...doc, cells };
}

/** Adds `color` to the palette if it is new and there is room (so files stay readable). */
export function withPaletteColor(doc: PixelDoc, color: RGBA): PixelDoc {
  if (color[3] === 0) return doc;
  const hex = toHex(color);
  if (doc.palette.length >= PALETTE_CHARS.length) return doc;
  if (doc.palette.some((c) => { const p = parseHex(c); return p !== null && toHex(p) === hex; })) return doc;
  return { ...doc, palette: [...doc.palette, hex] };
}

/** Zoom steps: fractions for big canvases, whole numbers for pixel art. */
export const ZOOM_LEVELS = [0.0625, 0.125, 0.25, 0.5, 1, 2, 3, 4, 6, 8, 12, 16, 20, 24, 32, 40, 48, 64];

export function stepZoom(zoom: number, dir: 1 | -1): number {
  if (dir > 0) return ZOOM_LEVELS.find((l) => l > zoom + 1e-6) ?? ZOOM_LEVELS.at(-1)!;
  return [...ZOOM_LEVELS].reverse().find((l) => l < zoom - 1e-6) ?? ZOOM_LEVELS[0]!;
}

/** Largest zoom level that fits a `w`×`h` canvas in `vw`×`vh` pixels. */
export function fitLevel(w: number, h: number, vw: number, vh: number): number {
  const z = Math.min(vw / w, vh / h);
  return [...ZOOM_LEVELS].reverse().find((l) => l <= z) ?? ZOOM_LEVELS[0]!;
}

function selectionRect(s: PixelState): PixelSelection | null {
  const { doc, selection } = s;
  if (!doc) return null;
  return selection ?? { x: 0, y: 0, w: doc.width, h: doc.height };
}

export const usePixel = create<PixelState>((set, get) => {
  function scheduleSave(): void {
    if (saveTimer) clearTimeout(saveTimer);
    set({ saveState: 'pending' });
    saveTimer = setTimeout(() => {
      saveTimer = null;
      void get().flush();
    }, SAVE_DELAY_MS);
  }

  function load(path: string, doc: PixelDoc, text: string) {
    lastDisk.set(path, text);
    setOverlay(path, text);
    set({
      path,
      doc,
      loading: false,
      error: null,
      past: [],
      future: [],
      frame: 0,
      layer: Math.min(get().layer, doc.layers.length - 1),
      selection: null,
      playing: false,
      previewAnimation: null,
      saveState: 'saved',
      zoom: fitLevel(doc.width, doc.height, 512, 512),
    });
  }

  /** Applies `fn` to a copy of the current cell and commits it. */
  function editCell(fn: (cell: Uint8ClampedArray, doc: PixelDoc) => Uint8ClampedArray | void): void {
    const { doc, frame, layer } = get();
    if (!doc) return;
    const src = doc.cells[frame]?.[layer];
    if (!src) return;
    const copy = new Uint8ClampedArray(src);
    const out = fn(copy, doc) ?? copy;
    get().commit(withCell(doc, frame, layer, out));
  }

  return {
    path: null,
    doc: null,
    loading: false,
    error: null,
    saveState: 'saved',
    past: [],
    future: [],
    frame: 0,
    layer: 0,
    tool: 'pencil',
    color: [26, 28, 44, 255],
    secondary: [244, 244, 244, 255],
    brush: 1,
    mirrorX: false,
    mirrorY: false,
    filled: false,
    contiguous: true,
    zoom: 16,
    grid: true,
    onion: false,
    tiled: false,
    playing: false,
    previewAnimation: null,
    selection: null,
    clipboard: null,

    open: async (path) => {
      const pid = projectId();
      if (!pid) return;
      await get().flush();
      set({ path, loading: true, error: null });
      try {
        const text = await docIO().read(pid, path);
        if (get().path !== path) return;
        load(path, parsePixelDoc(text), text);
      } catch (error) {
        if (get().path !== path) return;
        set({ doc: null, loading: false, error: `${path}: ${error instanceof Error ? error.message : String(error)}` });
      }
    },

    create: async (path, width, height, init) => {
      const pid = projectId();
      if (!pid) return;
      await get().flush();
      const base = newPixelDoc(width, height);
      const doc = init ? init(base) : base;
      const text = serializePixelDoc(doc);
      try {
        await docIO().write(pid, path, text);
      } catch (error) {
        logEditor('error', `Could not create ${path}: ${error instanceof Error ? error.message : error}`);
        return;
      }
      load(path, doc, text);
    },

    close: () => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      const { path } = get();
      if (path) clearOverlay(path);
      set({ path: null, doc: null, error: null, past: [], future: [], saveState: 'saved', selection: null, playing: false });
    },

    flush: async () => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      const { path, doc, saveState } = get();
      const pid = projectId();
      if (!path || !doc || !pid || saveState === 'saved') return;
      const text = serializePixelDoc(doc);
      if (text === lastDisk.get(path)) {
        set({ saveState: 'saved' });
        return;
      }
      set({ saveState: 'saving' });
      try {
        lastDisk.set(path, text);
        await docIO().write(pid, path, text);
        if (get().path === path && get().saveState === 'saving') set({ saveState: 'saved' });
      } catch (error) {
        lastDisk.delete(path);
        set({ saveState: 'error' });
        logEditor('error', `Could not save ${path}: ${error instanceof Error ? error.message : error}`);
      }
    },

    externalChange: async (path) => {
      const pid = projectId();
      if (!pid || path !== get().path) return;
      let text: string;
      try {
        text = await docIO().read(pid, path);
      } catch {
        return;
      }
      if (get().path !== path || text === lastDisk.get(path)) return;
      const doc = get().doc;
      if (doc && text === serializePixelDoc(doc)) return;
      try {
        const parsed = parsePixelDoc(text);
        const prev = get().doc;
        lastDisk.set(path, text);
        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = null;
        set({
          doc: parsed,
          error: null,
          saveState: 'saved',
          past: prev ? [...get().past, prev].slice(-historyLimit(parsed)) : get().past,
          future: [],
          frame: Math.min(get().frame, parsed.cells.length - 1),
          layer: Math.min(get().layer, parsed.layers.length - 1),
        });
        pushToViews(path, text);
      } catch (error) {
        set({ error: `${path}: ${error instanceof Error ? error.message : String(error)}` });
      }
    },

    commit: (doc, options = {}) => {
      const { history = true } = options;
      const state = get();
      const prev = options.base ?? state.doc;
      set({
        doc,
        past: history && prev ? [...state.past, prev].slice(-historyLimit(doc)) : state.past,
        future: history ? [] : state.future,
        frame: Math.min(state.frame, doc.cells.length - 1),
        layer: Math.min(state.layer, doc.layers.length - 1),
      });
      if (history && state.path) {
        pushToViews(state.path, serializePixelDoc(doc));
        scheduleSave();
      }
    },

    undo: () => {
      const { past, doc, future, path } = get();
      const prev = past.at(-1);
      if (!prev || !doc) return;
      set({ past: past.slice(0, -1), future: [doc, ...future].slice(0, historyLimit(doc)), doc: prev });
      set({ frame: Math.min(get().frame, prev.cells.length - 1), layer: Math.min(get().layer, prev.layers.length - 1) });
      if (path) {
        pushToViews(path, serializePixelDoc(prev));
        scheduleSave();
      }
    },

    redo: () => {
      const { past, doc, future, path } = get();
      const next = future[0];
      if (!next || !doc) return;
      set({ past: [...past, doc].slice(-historyLimit(doc)), future: future.slice(1), doc: next });
      set({ frame: Math.min(get().frame, next.cells.length - 1), layer: Math.min(get().layer, next.layers.length - 1) });
      if (path) {
        pushToViews(path, serializePixelDoc(next));
        scheduleSave();
      }
    },

    set: (patch) => set(patch),
    setFrame: (frame) => {
      const doc = get().doc;
      if (!doc) return;
      set({ frame: Math.max(0, Math.min(doc.cells.length - 1, frame)) });
    },
    setLayer: (layer) => {
      const doc = get().doc;
      if (!doc) return;
      set({ layer: Math.max(0, Math.min(doc.layers.length - 1, layer)) });
    },
    swapColors: () => set({ color: get().secondary, secondary: get().color }),

    addFrame: (duplicate) => {
      const { doc, frame } = get();
      if (!doc) return;
      const fresh = doc.layers.map((_, i) => (duplicate ? new Uint8ClampedArray(doc.cells[frame]![i]!) : emptyCell(doc)));
      const cells = [...doc.cells.slice(0, frame + 1), fresh, ...doc.cells.slice(frame + 1)];
      // Animations keep pointing at the same art.
      const animations = Object.fromEntries(
        Object.entries(doc.animations).map(([k, a]) => [k, { ...a, frames: a.frames.map((f) => (f > frame ? f + 1 : f)) }]),
      );
      get().commit({ ...doc, cells, animations });
      set({ frame: frame + 1 });
    },

    deleteFrame: () => {
      const { doc, frame } = get();
      if (!doc || doc.cells.length <= 1) return;
      const cells = doc.cells.filter((_, i) => i !== frame);
      const animations = Object.fromEntries(
        Object.entries(doc.animations).map(([k, a]) => [
          k,
          { ...a, frames: a.frames.filter((f) => f !== frame).map((f) => (f > frame ? f - 1 : f)) },
        ]),
      );
      get().commit({ ...doc, cells, animations });
      set({ frame: Math.min(frame, cells.length - 1) });
    },

    moveFrame: (delta) => {
      const { doc, frame } = get();
      if (!doc) return;
      const to = frame + delta;
      if (to < 0 || to >= doc.cells.length) return;
      const cells = [...doc.cells];
      [cells[frame], cells[to]] = [cells[to]!, cells[frame]!];
      const swap = (f: number) => (f === frame ? to : f === to ? frame : f);
      const animations = Object.fromEntries(
        Object.entries(doc.animations).map(([k, a]) => [k, { ...a, frames: a.frames.map(swap) }]),
      );
      get().commit({ ...doc, cells, animations });
      set({ frame: to });
    },

    addLayer: () => {
      const { doc, layer } = get();
      if (!doc) return;
      const at = layer + 1;
      const layers = [...doc.layers.slice(0, at), { name: `Layer ${doc.layers.length + 1}`, visible: true, opacity: 1 }, ...doc.layers.slice(at)];
      const cells = doc.cells.map((f) => [...f.slice(0, at), emptyCell(doc), ...f.slice(at)]);
      get().commit({ ...doc, layers, cells });
      set({ layer: at });
    },

    deleteLayer: () => {
      const { doc, layer } = get();
      if (!doc || doc.layers.length <= 1) return;
      const layers = doc.layers.filter((_, i) => i !== layer);
      const cells = doc.cells.map((f) => f.filter((_, i) => i !== layer));
      get().commit({ ...doc, layers, cells });
      set({ layer: Math.min(layer, layers.length - 1) });
    },

    moveLayer: (delta) => {
      const { doc, layer } = get();
      if (!doc) return;
      const to = layer + delta;
      if (to < 0 || to >= doc.layers.length) return;
      const swap = <T,>(list: T[]) => {
        const out = [...list];
        [out[layer], out[to]] = [out[to]!, out[layer]!];
        return out;
      };
      get().commit({ ...doc, layers: swap(doc.layers), cells: doc.cells.map(swap) });
      set({ layer: to });
    },

    setLayerProps: (index, patch) => {
      const { doc } = get();
      if (!doc || !doc.layers[index]) return;
      get().commit({ ...doc, layers: doc.layers.map((l, i) => (i === index ? { ...l, ...patch } : l)) });
    },

    mergeDown: () => {
      const { doc, layer } = get();
      if (!doc || layer === 0) return;
      const top = doc.layers[layer]!;
      const cells = doc.cells.map((f) => {
        const below = new Uint8ClampedArray(f[layer - 1]!);
        if (top.visible) {
          const src = f[layer]!;
          for (let i = 0; i < below.length; i += 4) {
            const sa = (src[i + 3]! / 255) * top.opacity;
            if (sa <= 0) continue;
            const da = below[i + 3]! / 255;
            const oa = sa + da * (1 - sa);
            for (let c = 0; c < 3; c++) below[i + c] = (src[i + c]! * sa + below[i + c]! * da * (1 - sa)) / oa;
            below[i + 3] = oa * 255;
          }
        }
        return [...f.slice(0, layer - 1), below, ...f.slice(layer + 1)];
      });
      get().commit({ ...doc, layers: doc.layers.filter((_, i) => i !== layer), cells });
      set({ layer: layer - 1 });
    },

    addPaletteColor: (hex) => {
      const { doc } = get();
      const c = parseHex(hex);
      if (!doc || !c) return;
      const next = withPaletteColor(doc, c);
      if (next !== doc) get().commit(next);
    },

    removePaletteColor: (index) => {
      const { doc } = get();
      if (!doc) return;
      // Rows refer to palette indices; serialization re-indexes, and art
      // keeps its colors (cells store RGBA in memory).
      get().commit({ ...doc, palette: doc.palette.filter((_, i) => i !== index) });
    },

    setPaletteColor: (index, hex) => {
      const { doc } = get();
      const to = parseHex(hex);
      const from = doc ? parseHex(doc.palette[index] ?? '') : null;
      if (!doc || !to || !from) return;
      // Recolor every pixel that used the old palette color.
      const cells = doc.cells.map((f) =>
        f.map((cell) => {
          let out: Uint8ClampedArray | null = null;
          for (let i = 0; i < cell.length; i += 4) {
            if (cell[i + 3] === 0) continue;
            if (cell[i] === from[0] && cell[i + 1] === from[1] && cell[i + 2] === from[2] && cell[i + 3] === from[3]) {
              out ??= new Uint8ClampedArray(cell);
              out.set(to, i);
            }
          }
          return out ?? cell;
        }),
      );
      get().commit({ ...doc, cells, palette: doc.palette.map((c, i) => (i === index ? toHex(to) : c)) });
      if (toHex(get().color) === toHex(from)) set({ color: to });
    },

    paletteFromArt: () => {
      const { doc } = get();
      if (!doc) return;
      get().commit({ ...doc, palette: usedColors(doc) });
    },

    setAnimation: (name, anim) => {
      const { doc } = get();
      if (!doc || !name.trim()) return;
      const animations = { ...doc.animations };
      if (anim) animations[name] = anim;
      else delete animations[name];
      get().commit({ ...doc, animations });
    },

    renameAnimation: (from, to) => {
      const { doc } = get();
      if (!doc || !to.trim() || from === to || !doc.animations[from] || doc.animations[to]) return;
      const animations: Record<string, PixelAnimation> = {};
      for (const [k, a] of Object.entries(doc.animations)) animations[k === from ? to : k] = a;
      get().commit({ ...doc, animations });
      if (get().previewAnimation === from) set({ previewAnimation: to });
    },

    setPivot: (pivot) => {
      const { doc } = get();
      if (!doc) return;
      const next = { ...doc };
      if (pivot) next.pivot = pivot;
      else delete next.pivot;
      get().commit(next);
    },

    setFilter: (filter) => {
      const { doc } = get();
      if (!doc) return;
      const next = { ...doc };
      if (filter) next.filter = filter;
      else delete next.filter;
      get().commit(next);
    },

    resize: (width, height, anchor) => {
      const { doc } = get();
      if (!doc || width < 1 || height < 1 || (width === doc.width && height === doc.height)) return;
      const to = { width, height };
      const cells = doc.cells.map((f) => f.map((c) => resizeCell(c, doc, to, anchor)));
      get().commit({ ...doc, width, height, cells });
      set({ selection: null, zoom: fitLevel(width, height, 512, 512) });
    },

    flip: (horizontal, allFrames) => {
      const { doc, frame, layer } = get();
      if (!doc) return;
      if (!allFrames) {
        get().commit(withCell(doc, frame, layer, flipCell(doc, doc.cells[frame]![layer]!, horizontal)));
        return;
      }
      get().commit({ ...doc, cells: doc.cells.map((f) => f.map((c) => flipCell(doc, c, horizontal))) });
    },

    shift: (dx, dy) => editCell((cell, doc) => shiftCell(doc, cell, dx, dy, true)),

    copySelection: () => {
      const s = get();
      const r = selectionRect(s);
      const cell = s.doc?.cells[s.frame]?.[s.layer];
      if (!r || !cell || !s.doc) return;
      const pixels = new Uint8ClampedArray(r.w * r.h * 4);
      for (let y = 0; y < r.h; y++)
        for (let x = 0; x < r.w; x++) pixels.set(getPixel(s.doc, cell, r.x + x, r.y + y), (y * r.w + x) * 4);
      set({ clipboard: { w: r.w, h: r.h, pixels } });
    },

    cutSelection: () => {
      get().copySelection();
      get().deleteSelection();
    },

    deleteSelection: () => {
      const r = selectionRect(get());
      if (!r) return;
      editCell((cell, doc) => {
        for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) setPixel(doc, cell, x, y, [0, 0, 0, 0]);
      });
    },

    paste: () => {
      const { clipboard, selection } = get();
      if (!clipboard) return;
      const ox = selection?.x ?? 0;
      const oy = selection?.y ?? 0;
      editCell((cell, doc) => {
        for (let y = 0; y < clipboard.h; y++)
          for (let x = 0; x < clipboard.w; x++) {
            const o = (y * clipboard.w + x) * 4;
            if (clipboard.pixels[o + 3] === 0) continue;
            setPixel(doc, cell, ox + x, oy + y, [clipboard.pixels[o]!, clipboard.pixels[o + 1]!, clipboard.pixels[o + 2]!, clipboard.pixels[o + 3]!]);
          }
      });
      set({ selection: { x: ox, y: oy, w: clipboard.w, h: clipboard.h } });
    },
  };
});
